/**
 * Row-identity contracts: the provider-agnostic notion of a value that
 * *identifies a row* (an instance reference, an entity key, …) lives here,
 * in the TYPE layer. The engine never hard-codes what an identity looks
 * like; each provider contributes a {@link RowIdentityType} describing how to
 * recognise, compare, key, and scope its own identity values, and those
 * contracts are composed into an {@link IdentityTable} threaded through the
 * type-check / compile / eval passes.
 *
 * No provider specifics (CDF instance references, spaces, …) appear here —
 * a provider implements {@link RowIdentityType} in its own module.
 */

/**
 * A provider-supplied contract describing one family of row-identifying
 * values. Recognition (`is`), equality (`equals`), dedup keying (`key`),
 * and scoping (`scopeId`) are the only questions the engine ever asks of an
 * identity; everything provider-specific is encapsulated behind them.
 */
export interface RowIdentityType {
  /** Stable id of this identity contract, unique within an {@link IdentityTable}. */
  readonly id: string;
  /** True iff `value` is a row-identifying value of this family. */
  readonly is: (value: unknown) => boolean;
  /** Identity equality between two values of this family. */
  readonly equals: (a: unknown, b: unknown) => boolean;
  /** A stable dedup/cache key for a value of this family. */
  readonly key: (value: unknown) => string;
  /** The id of the scope (e.g. partition / space) a value belongs to. */
  readonly scopeId: (value: unknown) => string;
}

/**
 * A composed, immutable view over a set of {@link RowIdentityType} contracts.
 * Built from a list at the provider/session boundary and passed by value —
 * there is no global mutable registry. Answers the two questions the engine
 * needs: "which contract, if any, claims this value?" and "fetch the contract
 * with this id".
 */
export interface IdentityTable {
  /** The first contract whose `is` claims `value`, else `undefined`. */
  readonly claimingIdentity: (value: unknown) => RowIdentityType | undefined;
  /** Look up a contract by its {@link RowIdentityType.id}, else `undefined`. */
  readonly identityById: (id: string) => RowIdentityType | undefined;
}

/**
 * Compose a list of identity contracts into an {@link IdentityTable}. The
 * `claimingIdentity` resolver tries each contract in order; `identityById`
 * is an exact id lookup. Last id wins on duplicate ids in the by-id map,
 * while `claimingIdentity` keeps declared order — callers compose a
 * deduplicated list at the boundary.
 */
export const createIdentityTable = (identities: readonly RowIdentityType[]): IdentityTable => {
  const byId = new Map(identities.map((identity) => [identity.id, identity] as const));
  return {
    claimingIdentity: (value) => identities.find((identity) => identity.is(value)),
    identityById: (id) => byId.get(id),
  };
};

/**
 * The identity-free table: claims nothing, resolves no id. Used as the
 * benign default wherever an {@link IdentityTable} is threaded but no
 * provider has contributed any identity contracts (`defaultEvalCtx`,
 * isolated unit tests, the type-check pass without a session). Threading
 * it is a no-op — the engine asks the same questions, the answers are
 * always "no identity".
 */
export const EMPTY_IDENTITY_TABLE: IdentityTable = createIdentityTable([]);
